// src/lib/http/refresh.ts
// Keeps the cookie session alive (contract §6.4, §6.10): one refresh at a time across tabs, at 80 % of the access token's life and on a 401, activity-gated.

import { refresh as postRefresh } from "../../api/auth/authApi";
import { applyRefresh, getAuthSession, isSignedIn, loadAuthSession, revalidateAuthSession, sessionTimesReceivedAt, setAnonymous, subscribeAuthSession, type AuthSessionState, type SessionTimes } from "./session";

export const SESSION_CHANNEL = "xoe-session";
const LOCK_NAME = "xoe-session-refresh";
// Written inside the lock so a tab that waited for it sees the rotation even before the channel message lands.
const SHARED_REFRESHED_AT = "xoe-session-refreshed-at";

export const ACCESS_TOKEN_LIFETIME_MS = 600_000;
const REFRESH_FRACTION = 0.8;
const MIN_REFRESH_DELAY_MS = 5_000;
// A session's expiry further out than the token lifetime (plus skew) means the clocks disagree; trust the constant instead.
const SKEW_ALLOWANCE_MS = 60_000;
// The idle deadline's last minute: input then is sent at once, and a refusal then is an idle sign-out.
const IDLE_MARGIN_MS = 60_000;

// `signed_in` is SPA-internal (contract §6.10 names the other three): a sign-in replaced the shared cookies, perhaps as someone else.
export type SessionMessage = { type: "refreshed"; session?: SessionTimes } | { type: "logged_out" } | { type: "activity" } | { type: "signed_in" };

// Every pointer, key, wheel, touch and mouse movement counts as presence (D4); scroll is on document because it does not bubble.
const WINDOW_INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"] as const;
const INPUT_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

// Codes that send the user to sign in without a retry; the refreshable ones get one single-flight refresh.
export const TERMINAL_CODES = new Set(["session_revoked", "refresh_invalid", "refresh_reused", "account_inactive"]);
export const REFRESHABLE_CODES = new Set(["unauthenticated", "token_expired"]);

// Identity's own credential steps answer 401 for a wrong secret; refreshing there would mask it (contract §6.10).
export const CREDENTIAL_STEPS: readonly string[] = ["login", "refresh", "mfa/verify", "mfa/recover/[^/]+", "passkeys/login/[^/]+"];
// Anchored because requestPath() yields the bare pathname, so only identity's own /auth/ steps match.
const NO_REFRESH_PATH = new RegExp(`^/auth/(?:${CREDENTIAL_STEPS.join("|")})$`);

export const isRefreshExempt = (path: string): boolean => NO_REFRESH_PATH.test(path);

interface KeeperState {
	channel: BroadcastChannel | null;
	timer: ReturnType<typeof setTimeout> | null;
	refreshDueAt: number | null;
	hadInput: boolean;
	lastRefreshAt: number;
	inFlight: Promise<boolean> | null;
	teardown: Array<() => void>;
}

const keeper: KeeperState = {
	channel: null,
	timer: null,
	refreshDueAt: null,
	hadInput: false,
	lastRefreshAt: 0,
	inFlight: null,
	teardown: [],
};

const post = (message: SessionMessage): void => {
	try {
		keeper.channel?.postMessage(message);
	} catch (error) {
		console.error("session.channel.post_failed", { type: message.type, name: (error as Error)?.name });
	}
};

const readSharedRefreshedAt = (): number => {
	try {
		return Number(window.localStorage.getItem(SHARED_REFRESHED_AT)) || 0;
	} catch {
		return 0;
	}
};

const writeSharedRefreshedAt = (at: number): void => {
	try {
		window.localStorage.setItem(SHARED_REFRESHED_AT, String(at));
	} catch {
		// Storage can be disabled; the channel message still reaches the other tabs.
	}
};

const lastRotation = (): number => Math.max(keeper.lastRefreshAt, readSharedRefreshedAt());

const clearTimer = (): void => {
	if (keeper.timer !== null) clearTimeout(keeper.timer);
	keeper.timer = null;
	keeper.refreshDueAt = null;
};

const remainingLifetime = (accessExpiresAt: string | undefined, now: number): number => {
	const expires = accessExpiresAt ? Date.parse(accessExpiresAt) : NaN;
	const remaining = expires - now;
	return Number.isFinite(remaining) && remaining > 0 && remaining <= ACCESS_TOKEN_LIFETIME_MS + SKEW_ALLOWANCE_MS ? remaining : ACCESS_TOKEN_LIFETIME_MS;
};

// The timer refreshes only when there was input since the last refresh; otherwise the token lapses and the idle limit runs (§6.10).
const onTimer = (): void => {
	keeper.timer = null;
	if (isSignedIn() && keeper.hadInput) void refreshSession();
};

export const scheduleRefresh = (accessExpiresAt: string | undefined, now: number = Date.now()): void => {
	clearTimer();
	const delay = Math.max(MIN_REFRESH_DELAY_MS, Math.floor(remainingLifetime(accessExpiresAt, now) * REFRESH_FRACTION));
	keeper.refreshDueAt = now + delay;
	keeper.timer = setTimeout(onTimer, delay);
};

const ownsNoSession = (status: AuthSessionState["status"]): boolean => status === "anonymous" || status === "unknown";

// A terminal answer ends the session here; `broadcast` tells the other tabs (they share the now-cleared cookies).
export const endSession = (reason: string, { broadcast }: { broadcast: boolean }): void => {
	clearTimer();
	keeper.hadInput = false;
	if (broadcast) post({ type: "logged_out" });
	setAnonymous(reason);
};

// identity refuses a lapsed session with its generic code; the last-known times name the limit that ended it (session_idle / session_absolute).
const endedReasonFor = (code: string, now: number = Date.now()): string => {
	const times = getAuthSession().info?.session;
	// A deadline already past when this tab received it means the clocks disagree, so the server's code stands.
	const passed = (deadline: string | undefined, marginMs = 0): boolean => {
		const at = Date.parse(deadline ?? "");
		return at >= sessionTimesReceivedAt() - SKEW_ALLOWANCE_MS && now >= at - marginMs;
	};
	if (passed(times?.absolute_expires_at)) return "session_absolute";
	if (passed(times?.idle_expires_at, IDLE_MARGIN_MS)) return "session_idle";
	return code;
};

const errorStatus = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;
const errorCode = (error: unknown): string | undefined => {
	const code = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
	return typeof code === "string" ? code : undefined;
};

const runRefresh = async (since: number): Promise<boolean> => {
	// Another tab rotated the cookie after this request left; its new access cookie is already ours.
	if (lastRotation() > since) return true;
	const activity = keeper.hadInput;
	keeper.hadInput = false;
	try {
		const response = await postRefresh(activity);
		const at = Date.now();
		keeper.lastRefreshAt = at;
		writeSharedRefreshedAt(at);
		const session = response?.data?.session;
		post({ type: "refreshed", session });
		if (session) applyRefresh(response.data);
		scheduleRefresh(session?.access_expires_at, at);
		return true;
	} catch (error) {
		keeper.hadInput = keeper.hadInput || activity;
		const status = errorStatus(error);
		const code = errorCode(error);
		// identity clears both cookies on every 401/403 here, so the session is over for every tab.
		if (status === 401 || status === 403) {
			console.warn("session.refresh.rejected", { status, code });
			endSession(endedReasonFor(code ?? "refresh_invalid"), { broadcast: isSignedIn() });
			return false;
		}
		// 503 degraded, 429 or the network: the access cookie may still be good, so the session is left alone.
		console.error("session.refresh.failed", { status, code });
		return false;
	}
};

const withCrossTabLock = <T>(fn: () => Promise<T>): Promise<T> => {
	const locks = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
	if (!locks?.request) return fn();
	return locks.request(LOCK_NAME, { mode: "exclusive" }, fn) as Promise<T>;
};

// Single-flight in this tab, one-at-a-time across tabs; `since` is when the failing request was sent.
export const refreshSession = ({ since = Date.now() }: { since?: number } = {}): Promise<boolean> => {
	if (lastRotation() > since) return Promise.resolve(true);
	if (!keeper.inFlight) {
		keeper.inFlight = withCrossTabLock(() => runRefresh(since)).finally(() => {
			keeper.inFlight = null;
		});
	}
	return keeper.inFlight;
};

// The 401/403 policy of contract §6.10 for one failed same-origin request; true means "retry it once".
export const recoverFromUnauthorized = async (status: number, code: string | undefined, path: string, sentAt: number): Promise<boolean> => {
	if (isRefreshExempt(path)) return false;
	if (code && TERMINAL_CODES.has(code)) {
		endSession(endedReasonFor(code), { broadcast: isSignedIn() });
		return false;
	}
	if (status !== 401 || !code || !REFRESHABLE_CODES.has(code)) return false;
	return refreshSession({ since: sentAt });
};

// No refresh is due before the idle deadline's last minute, so the next one would carry this input too late.
const timerTooLateForIdle = (): boolean => {
	const times = getAuthSession().info?.session;
	const idle = Date.parse(times?.idle_expires_at ?? "");
	// Capped at the absolute limit the deadline cannot move, so refreshing here would only repeat on every input.
	if (!Number.isFinite(idle) || idle >= Date.parse(times?.absolute_expires_at ?? "")) return false;
	return keeper.refreshDueAt === null || keeper.refreshDueAt >= idle - IDLE_MARGIN_MS;
};

export const noteActivity = (): void => {
	if (keeper.hadInput || !isSignedIn()) return;
	keeper.hadInput = true;
	post({ type: "activity" });
	if (timerTooLateForIdle()) void refreshSession();
};

const onMessage = (event: MessageEvent<SessionMessage>): void => {
	switch (event.data?.type) {
		case "refreshed":
			keeper.lastRefreshAt = Date.now();
			keeper.hadInput = false;
			if (!isSignedIn()) break;
			if (event.data.session) applyRefresh({ session: event.data.session });
			scheduleRefresh(event.data.session?.access_expires_at);
			break;
		case "logged_out":
			if (!ownsNoSession(getAuthSession().status)) endSession("logged_out", { broadcast: false });
			break;
		case "activity":
			keeper.hadInput = true;
			break;
		case "signed_in":
			keeper.lastRefreshAt = Date.now();
			loadAuthSession().catch(() => {
				// Logged in loadAuthSession; this tab keeps what it had until the next check.
			});
			break;
		default:
			break;
	}
};

// Focus or a visible tab counts as presence; a tab woken past its refresh point (timers are throttled in the background) refreshes now.
const onWake = (): void => {
	if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
	if (!isSignedIn()) return;
	// Another tab may have signed in as someone else; the cookies are shared, so re-read who this tab is.
	void revalidateAuthSession();
	noteActivity();
	if (keeper.hadInput && keeper.refreshDueAt !== null && Date.now() >= keeper.refreshDueAt) void refreshSession();
};

const onAuthChange = (next: AuthSessionState, previous: AuthSessionState): void => {
	if (next.status !== "authenticated") {
		clearTimer();
		return;
	}
	const expires = next.info?.session?.access_expires_at;
	if (expires !== previous.info?.session?.access_expires_at || previous.status !== "authenticated") scheduleRefresh(expires);
};

export const startSessionKeeper = (): void => {
	if (keeper.teardown.length > 0) return;
	if (typeof BroadcastChannel !== "undefined") {
		keeper.channel = new BroadcastChannel(SESSION_CHANNEL);
		keeper.channel.addEventListener("message", onMessage as EventListener);
		keeper.teardown.push(() => {
			keeper.channel?.close();
			keeper.channel = null;
		});
	}
	if (typeof window !== "undefined") {
		const input = () => noteActivity();
		WINDOW_INPUT_EVENTS.forEach((type) => window.addEventListener(type, input, INPUT_OPTIONS));
		document.addEventListener("scroll", input, INPUT_OPTIONS);
		window.addEventListener("focus", onWake);
		document.addEventListener("visibilitychange", onWake);
		keeper.teardown.push(() => {
			WINDOW_INPUT_EVENTS.forEach((type) => window.removeEventListener(type, input, { capture: true }));
			document.removeEventListener("scroll", input, { capture: true });
			window.removeEventListener("focus", onWake);
			document.removeEventListener("visibilitychange", onWake);
		});
	}
	keeper.teardown.push(subscribeAuthSession(onAuthChange));
	const current = getAuthSession();
	if (current.status === "authenticated") scheduleRefresh(current.info?.session?.access_expires_at);
};

export const announceLogout = (): void => post({ type: "logged_out" });

export const announceSignIn = (): void => post({ type: "signed_in" });

// Tests only: drop every listener, timer and flag.
export const resetSessionKeeper = (): void => {
	keeper.teardown.splice(0).forEach((undo) => undo());
	clearTimer();
	keeper.hadInput = false;
	keeper.lastRefreshAt = 0;
	keeper.inFlight = null;
	try {
		window.localStorage.removeItem(SHARED_REFRESHED_AT);
	} catch {
		// nothing to clear
	}
};

export const sessionKeeperSnapshot = () => ({ hadInput: keeper.hadInput, refreshDueAt: keeper.refreshDueAt, lastRefreshAt: keeper.lastRefreshAt });
